import { Button, notification } from 'antd';
import { CSSProperties } from 'react';

import { PaymentAvailable } from '@common/components/PaymentButton/PaymentButton';
import { EDITABLE_IME_STATUS, EE_STATUS, EXPIRED_NOTIFICATION_KEY, IME_STATUS, STATUS } from '@common/constants';
import { EstimateStatus, GetEstimateDto } from '@common/models/save-estimate-dto';
import { NotifierType, SimpleMessage, StatusError, showNotification } from '@core/components';
import i18n from '@core/i18n/i18n';

interface NotifyFromStatusParams {
    estimateStatus: EstimateStatus;
    fetchedEstimate: GetEstimateDto;
    expiredCloneAction?: () => void;
    expiredNewAction?: () => void;
}

/**
 * Test if the estimate is expired or closed, put up a message allowing the user to clone/start fresh/ignore
 * @param estimateStatus
 * @param expiredCloneAction
 * @param expiredNewAction
 */
const handleExpiredOrClosed = ({
    estimateStatus,
    expiredCloneAction,
    expiredNewAction,
}: Omit<NotifyFromStatusParams, 'fetchedEstimate'>): EE_STATUS[] | undefined => {
    if (![STATUS.CLOSED, STATUS.EXPIRED].some((s) => estimateStatus.status === s)) {
        return;
    }

    const basePath = 'NOTIFICATIONS.FETCH_ESTIMATE';
    const path = `${basePath}.${estimateStatus.status === STATUS.CLOSED ? 'CLOSED' : 'EXPIRED'}`;
    const buttonStyle: CSSProperties = { margin: '10px' };

    showNotification({
        type: NotifierType.ERROR,
        key: EXPIRED_NOTIFICATION_KEY,
        title: i18n.t(`${path}.TITLE`),
        description: (
            <StatusError description={i18n.t(`${path}.DESC`)} message={i18n.t(`${path}.MSG`)}>
                {expiredCloneAction && (
                    <Button
                        onClick={() => {
                            notification.close(EXPIRED_NOTIFICATION_KEY);
                            expiredCloneAction();
                        }}
                        type="primary"
                        style={buttonStyle}
                    >
                        {i18n.t(`${basePath}.EXPIRED.COPY_BUTTON`).toString()}
                    </Button>
                )}
                {expiredNewAction && (
                    <Button
                        onClick={() => {
                            notification.close(EXPIRED_NOTIFICATION_KEY);
                            expiredNewAction();
                        }}
                        type="primary"
                        style={buttonStyle}
                    >
                        {i18n.t(`${basePath}.EXPIRED.NEW_BUTTON`).toString()}
                    </Button>
                )}
            </StatusError>
        ),
        duration: 0,
    });

    // nothing else matters, it's closed
    return [EE_STATUS.CLOSED, EE_STATUS.NO_SAVE];
};

const handleReadyForPayment = (
    estimateStatus: EstimateStatus,
    fetchedEstimate: GetEstimateDto
): EE_STATUS[] | undefined => {
    const paymentUrl = getBestPaymentUrl(estimateStatus, fetchedEstimate);
    if (!paymentUrl || !isReadyForPayment(estimateStatus, fetchedEstimate)) {
        return;
    }
    // TODO after EZE-474 is merged, ensure that we're on the last revision before allowing payment
    showNotification({
        type: NotifierType.SUCCESS,
        title: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.READY_TO_PAY.TITLE'),
        description: <PaymentAvailable paymentUrl={paymentUrl} estimate={fetchedEstimate} />,
        duration: 60,
    });
    return [EE_STATUS.CAN_PAY];
};

/**
 * Generates notifications based on a status or status change
 * @param estimateStatus
 * @param fetchedEstimate
 * @param expiredCloneAction
 * @param expiredNewAction
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const notifyFromStatus = ({
    estimateStatus,
    fetchedEstimate,
    expiredCloneAction,
    expiredNewAction,
}: // eslint-disable-next-line sonarjs/cognitive-complexity
NotifyFromStatusParams): EE_STATUS[] => {
    //TODO KC This has gotten out of hand - it started out so simple!

    if (!estimateStatus.status || !fetchedEstimate?.estimateNumber) {
        return [EE_STATUS.OPEN];
    }

    // closed or expired
    const expiredStatus = handleExpiredOrClosed({ estimateStatus, expiredCloneAction, expiredNewAction });
    if (expiredStatus) {
        return expiredStatus;
    }

    // has the status changed since loaded?
    const statusUpdated = estimateStatus.status !== fetchedEstimate.status;

    // at this point, we know we're in an OPEN state
    const returnStatus = [EE_STATUS.OPEN];

    const paymentStatus = handleReadyForPayment(estimateStatus, fetchedEstimate);
    if (paymentStatus) {
        return [...returnStatus, ...paymentStatus];
    }

    if (statusUpdated) {
        const changeMessage = (from: string | null, to: string | null) => {
            if (from && to) {
                return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.FROM_TO', { from: from, to: to });
            }
            if (to) {
                return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TO', { from: from, to: to });
            }
            if (from) {
                return i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.FROM', { from: from, to: to });
            }
            return '';
        };

        showNotification({
            type: NotifierType.SUCCESS,
            title: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE'),
            description: (
                <SimpleMessage
                    description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.STATUS_UPDATED.TITLE')}
                    message={changeMessage(fetchedEstimate.status, estimateStatus.status)}
                />
            ),
            duration: 0,
        });
    }

    if (!isAppointmentEditable(estimateStatus)) {
        showNotification({
            type: NotifierType.WARNING,
            title: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.TITLE'),
            description: (
                <StatusError
                    description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.DESC', { ...estimateStatus })}
                    message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.APPOINTMENT_PENDING.MSG', { ...estimateStatus })}
                />
            ),
            duration: 0,
        });

        returnStatus.push(EE_STATUS.NO_SAVE);
    }

    // new error message
    if (estimateStatus.imeMessage) {
        showNotification({
            type: NotifierType.ERROR,
            title: i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.TITLE'),
            description: (
                <StatusError
                    description={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.DESC', { ...estimateStatus })}
                    message={i18n.t('NOTIFICATIONS.FETCH_ESTIMATE.EXTERNAL_ERROR.MSG', { ...estimateStatus })}
                />
            ),
            duration: 0,
        });

        returnStatus.push(EE_STATUS.ERROR);
    }

    return returnStatus;
};

export const getBestPaymentUrl = (
    status: EstimateStatus | undefined,
    estimate: GetEstimateDto | undefined,
    checkoutUrl?: string | undefined
): string | null => {
    return checkoutUrl ?? status?.paymentUrl ?? estimate?.paymentUrl ?? null;
};

export const isLatestRevision = (estimate: GetEstimateDto): boolean => {
    // is the given estimate the most recent revision of the chain?
    return (
        !!estimate.revisions?.length &&
        estimate.revisions[estimate.revisions.length - 1]?.estimateNumber === estimate?.estimateNumber
    );
};

export const isReadyForPayment = (status?: EstimateStatus, estimate?: GetEstimateDto): boolean => {
    // must have an estimate number and a payment url, either from the fetched estimate or status update
    const paymentUrl = getBestPaymentUrl(status, estimate);
    return (
        !!estimate?.estimateNumber &&
        !!paymentUrl &&
        // this status check could theoretically be incorrect if the status changes to hold after opening but that's unlikely
        [status?.status, estimate?.status].some((currentStatus) => currentStatus === STATUS.OPEN)
    );
};

/**
 * only certain IME STATUS values can successfully edit/save, all others get an alert
 * and probably restricted save options
 * @param status
 */
export const isAppointmentEditable = (status?: EstimateStatus): boolean => {
    return !status?.imeStatus || EDITABLE_IME_STATUS.includes(status?.imeStatus as IME_STATUS);
};

export const statusChanged = (prev: Partial<EstimateStatus>, curr: Partial<EstimateStatus>) => {
    const prevKeys = Object.keys(prev);
    const currentKeys = Object.keys(curr);
    if (!prevKeys.every((key) => currentKeys.includes(key)) || !currentKeys.every((key) => prevKeys.includes(key))) {
        return true;
    }
    const cMap = new Map([...Object.entries(curr)]);
    for (const [prevKey, prevValue] of Object.entries(prev)) {
        if (prevValue !== cMap.get(prevKey)) {
            return true;
        }
    }

    return false;
};
